// Chemins des endpoints publics présents dans les signatures (bannière et clic).
//
// Tous les paramètres sont dans le CHEMIN, jamais en query string : Netlify recopie la query string
// de la requête d'origine sur la destination des redirections (constaté le 13/09/2026), ce qui
// exposait l'adresse d'envoi ou l'identifiant de la personne au site de destination.
// Les anciennes formes (`?a=` et `?p=`) restent lues en repli : des signatures déjà installées
// à la main dans Apple Mail / Outlook peuvent encore les porter.

const decode = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** Segments du chemin après le nom de la fonction (`banner`, `go`), décodés. */
function segmentsAfter(url: URL, fn: string): string[] {
  const parts = url.pathname.split('/').filter(Boolean);
  const i = parts.indexOf(fn);
  return parts.slice(i + 1).map(decode);
}

const stripPng = (s: string) => s.replace(/\.png$/i, '');

/** `/b/<token>/<alias>.png` (Apple Mail / Outlook). Alias facultatif ; repli `?a=` — jamais d'alias en query string. */
export function bannerPath(base: string, publicToken: string, alias: string): string {
  return `${base}/b/${encodeURIComponent(publicToken)}/${encodeURIComponent(alias)}.png`;
}

/** `/go/p/<token>/<alias>` : clic sur une signature locale, campagne résolue au clic. */
export function localClickPath(base: string, publicToken: string, alias: string): string {
  return `${base}/go/p/${encodeURIComponent(publicToken)}/${encodeURIComponent(alias)}`;
}

/** `/go/<campaign_id>/<person_id>` : clic sur une signature Gmail, campagne figée. */
export function gmailClickPath(base: string, campaignId: string, personId: string): string {
  return `${base}/go/${encodeURIComponent(campaignId)}/${encodeURIComponent(personId)}`;
}

export interface BannerRoute {
  token: string;
  alias: string | null;
}

/**
 * Lecture de `/banner/<token>/<alias>.png` ; anciens formats acceptés :
 * `/banner/<token>.png?a=<alias>` et `/banner/<token>.png`.
 */
export function parseBannerRoute(url: URL): BannerRoute {
  const seg = segmentsAfter(url, 'banner');
  const token = stripPng(seg[0] ?? '');
  const alias = seg[1] ? stripPng(seg[1]) : url.searchParams.get('a');
  return { token, alias: alias || null };
}

export type GoRoute =
  | { kind: 'local'; token: string; alias: string | null }
  | { kind: 'gmail'; campaignId: string; personId: string | null }
  | { kind: 'none' };

/**
 * Lecture de `/go/p/<token>/<alias>` et `/go/<campaign_id>/<person_id>` ; anciens formats acceptés :
 * `/go/p/<token>?a=<alias>` et `/go/<campaign_id>?p=<person_id>`.
 */
export function parseGoRoute(url: URL): GoRoute {
  const seg = segmentsAfter(url, 'go');
  if (seg[0] === 'p') {
    if (!seg[1]) return { kind: 'none' };
    return { kind: 'local', token: seg[1], alias: seg[2] || url.searchParams.get('a') || null };
  }
  if (seg[0]) return { kind: 'gmail', campaignId: seg[0], personId: seg[1] || url.searchParams.get('p') || null };
  return { kind: 'none' };
}
